import type { PaymentData } from '@adyen/adyen-web';

import type { PaymentOption, PaymentRequest, PaymentType } from 'models/api';
import { is } from 'utils/helpers';

const PAYMENT_TYPES_WITH_COMPONENT = [
	'blik',
	'scheme',
	'swish',
] as const satisfies PaymentType[];

/**
 * Payment types that have an Adyen web component.
 */
export type PaymentComponentPaymentType =
	(typeof PAYMENT_TYPES_WITH_COMPONENT)[number];

/**
 * If the payment type has an Adyen web component.
 */
export function isPaymentTypeWithComponent(
	paymentType: PaymentType | undefined,
): paymentType is PaymentComponentPaymentType {
	return Boolean(
		paymentType && is.oneOf(paymentType, ...PAYMENT_TYPES_WITH_COMPONENT),
	);
}

const PAYMENT_TYPES_WITH_COMPONENT_STATE = [
	'blik',
	'scheme',
] as const satisfies PaymentComponentPaymentType[];

/**
 * Payment types that need state from the Adyen web components when making the
 * payment request.
 */
export type PaymentComponentWithStatePaymentType =
	(typeof PAYMENT_TYPES_WITH_COMPONENT_STATE)[number];

/**
 * If the payment type needs state from the Adyen web component when making
 * the payment request.
 */
export function isPaymentTypeWithComponentState(
	paymentType: PaymentType | undefined,
): paymentType is PaymentComponentWithStatePaymentType {
	return Boolean(
		paymentType && is.oneOf(paymentType, ...PAYMENT_TYPES_WITH_COMPONENT_STATE),
	);
}

export interface PaymentComponentState {
	data?: PaymentData;
	errors?: object;
	isValid?: boolean;
	valid?: object;
}

export function getPaymentRequest({
	paymentComponentState,
	returnUrl,
	selectedPayment,
}: {
	paymentComponentState: PaymentComponentState | undefined;
	returnUrl: string;
	selectedPayment: PaymentOption | undefined;
}): PaymentRequest {
	const request: PaymentRequest = {
		returnUrl,
		paymentMethod: {
			type: selectedPayment?.paymentMethodIdProvider,
		},
		storePaymentMethod: false,
		// https://docs.adyen.com/online-payments/3d-secure/api-reference/#browserinfo
		browserInfo: {
			// TODO: This should be done properly by having the server return it to
			// the client somewhere. It can't be returned in the HTML response though
			// because that may be cached and fetch requests don't use the default
			// accept header, so is it even possible? "This is typically used for
			// requests initiated via the address bar of a browser, or via an HTML
			// <a> element."
			// https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/Content_negotiation/List_of_default_Accept_values
			acceptHeader: '*/*',
			colorDepth: globalThis.screen.colorDepth,
			// navigator.javaEnabled is deprecated and will always return false
			// https://developer.mozilla.org/en-US/docs/Web/API/Navigator/javaEnabled
			javaEnabled: false,
			language: navigator.language,
			screenHeight: globalThis.screen.height,
			screenWidth: globalThis.screen.width,
			timeZoneOffset: String(new Date().getTimezoneOffset()),
			// Can be spoofed so it can possibly be removed as well, have a fallback.
			userAgent: navigator.userAgent || 'Unknown',
		},
	};

	if (
		isPaymentTypeWithComponentState(selectedPayment?.selectedPaymentType) &&
		paymentComponentState?.isValid
	) {
		request.paymentMethod = paymentComponentState.data?.paymentMethod;
	}

	return request;
}
